import { INITIAL_ITEMS_COUNT } from '../constants';
import { type TCodeData } from '../types';

import { resolveTimeZoneForIntl } from './utils';

/**
 * Sorts T-codes by severity (CRITICAL → HIGH → MEDIUM → LOW)
 * Items without severity are placed last
 */
export const sortTCodes = (tCodes: TCodeData[]): TCodeData[] => {
  // Severity order: CRITICAL (highest) → HIGH → MEDIUM → LOW → NONE → NORISK/LOW_RISK (lowest)
  const severityOrder: Record<
    'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE' | 'NORISK' | 'LOW_RISK',
    number
  > = {
    CRITICAL: 0,
    HIGH: 1,
    MEDIUM: 2,
    LOW: 3,
    NONE: 4,
    NORISK: 5,
    LOW_RISK: 6,
  };

  return [...tCodes].sort((a, b) => {
    // Sort by severity first (if available)
    const aSeverity = a.severity;
    const bSeverity = b.severity;

    // Items with severity come before items without severity
    if (aSeverity && !bSeverity) {
      return -1;
    }

    if (!aSeverity && bSeverity) {
      return 1;
    }

    // Both have severity: sort by severity order (CRITICAL → HIGH → MEDIUM → LOW)
    if (aSeverity && bSeverity) {
      const aOrder = severityOrder[aSeverity];
      const bOrder = severityOrder[bSeverity];

      // Compare severity order (CRITICAL=0, HIGH=1, MEDIUM=2, LOW=3)
      return aOrder - bOrder;
    }

    // If both are undefined or same severity, maintain original order
    return 0;
  });
};

/**
 * Paginates T-codes for display
 */
export const paginateTCodes = (
  tCodes: TCodeData[],
  page = 1,
  pageSize: number = INITIAL_ITEMS_COUNT
): { data: TCodeData[]; totalPages: number; currentPage: number } => {
  const totalPages = Math.ceil(tCodes.length / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;

  return {
    data: tCodes.slice(startIndex, endIndex),
    totalPages,
    currentPage: page,
  };
};

/**
 * Checks if content is scrollable
 */
export const isContentScrollable = (element: HTMLElement): boolean =>
  element.scrollHeight > element.clientHeight;

export const formatInTimeZoneGlobalDate = (
  iso?: string,
  timeZone?: string,
  displayFormat?: string
): string => {
  if (!iso || !timeZone) {
    return '-';
  }

  try {
    const { timeZone: ianaTimeZone, offsetMinutes } =
      resolveTimeZoneForIntl(timeZone);
    const baseDate = new Date(iso);
    const dateForFmt =
      offsetMinutes !== 0
        ? new Date(baseDate.getTime() + offsetMinutes * 60_000)
        : baseDate;
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: ianaTimeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(dateForFmt);

    const map: Record<string, string> = {};

    parts.forEach((p) => {
      if (p.type !== 'literal') {
        map[p.type] = p.value;
      }
    });

    // When a global-like display format is provided, shape the output accordingly.
    const pattern = displayFormat ?? 'dd/MM/yyyy, HH:mm';
    const day = map.day || '';
    const month = map.month || '';
    const year = map.year || '';
    const hour24 = Number(map.hour || '0');
    const minute = map.minute || '00';

    // Determine 12h vs 24h and day-first vs month-first
    const isMonthFirst = pattern.startsWith('MM/');
    const is12h = pattern.includes('h:mm');

    const displayHour = is12h
      ? String(hour24 % 12 || 12)
      : String(hour24).padStart(2, '0');
    const ampm = hour24 < 12 ? 'AM' : 'PM';

    const datePart = isMonthFirst
      ? `${month}/${day}/${year}`
      : `${day}/${month}/${year}`;

    const timePart = is12h
      ? `${displayHour}:${minute} ${ampm}`
      : `${displayHour}:${minute}`;

    return `${datePart}, ${timePart}`;
  } catch {
    return '-';
  }
};
